import { z } from "zod";

// Fact event mirroring Stripe state into the log. Stripe stays the source of
// truth; deciders never author billing. Recorded for replay/audit completeness.
export const stripeSubscriptionSynced = z.object({
  type: z.literal("StripeSubscriptionSynced"),
  billingAccountId: z.string().min(1),
  providerSubscriptionId: z.string().nullable().optional(),
  status: z.string(),
  plan: z.string().nullable().optional(),
  periodEnd: z.date().nullable().optional(),
});

// Audit-fact: admin granted/updated an entitlement override on a billing
// account.
export const billingEntitlementOverrideUpdated = z.object({
  type: z.literal("BillingEntitlementOverrideUpdated"),
  billingAccountId: z.string().min(1),
  enabled: z.boolean(),
  features: z.record(z.string(), z.unknown()).nullable().optional(),
  limits: z.record(z.string(), z.unknown()).nullable().optional(),
  reason: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
});

// Audit-fact: admin toggled a manager's subscription bypass.
export const subscriptionBypassUpdated = z.object({
  type: z.literal("SubscriptionBypassUpdated"),
  userPermissionId: z.string().min(1),
  enabled: z.boolean(),
  reason: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
});

// Audit-fact: a downgrade was scheduled at period end.
export const subscriptionDowngradeScheduled = z.object({
  type: z.literal("SubscriptionDowngradeScheduled"),
  subscriptionId: z.string().min(1),
  cancelAtPeriodEnd: z.boolean().nullable().optional(),
  currentPeriodEnd: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});

// Audit-fact: provider subscription quantity sync failed for a billing account
// (e.g. during create-site) but the operation was allowed to proceed. Recorded
// so an admin can review/reconcile. Reason is sanitized — no raw provider tokens.
export const subscriptionQuantitySyncFailed = z.object({
  type: z.literal("SubscriptionQuantitySyncFailed"),
  billingAccountId: z.string().min(1),
  siteId: z.string().min(1),
  expectedQuantity: z.number().int().nonnegative(),
  providerSubscriptionId: z.string().nullable().optional(),
  reason: z.string(),
});

// Audit-fact: admin manually triggered a subscription quantity resync for a
// billing account (e.g. to reconcile after a SubscriptionQuantitySyncFailed).
export const subscriptionQuantityResynced = z.object({
  type: z.literal("SubscriptionQuantityResynced"),
  billingAccountId: z.string().min(1),
  newQuantity: z.number().int().nonnegative(),
});

export const billingEvents = [
  stripeSubscriptionSynced,
  billingEntitlementOverrideUpdated,
  subscriptionBypassUpdated,
  subscriptionDowngradeScheduled,
  subscriptionQuantitySyncFailed,
  subscriptionQuantityResynced,
] as const;
